import { Base64 } from "js-base64";
import CONSOLE from "../utils/console";
import { ERROR_STORE, ErrorEnum } from "src/context/error-context";
import { ApiPublic } from "src/api/api_public";
import { ApiUser } from "src/api/api_user";

/**
 * The authentication request with a webauthn key
 *
 * @returns A promise with a wrapper Result and converted errors
 */
export async function authenticate() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const challenge: any = await ApiPublic.vpnAuth.loginWebauthn();

    const publicKey = challenge.publicKey;
    CONSOLE.debug("Received challenge from server", challenge);

    const rawCredential = await navigator.credentials.get({
        publicKey: {
            rpId: publicKey.rpId,
            timeout: publicKey.timeout,
            userVerification: publicKey.userVerification,
            challenge: Base64.toUint8Array(publicKey.challenge).slice(),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            allowCredentials: publicKey.allowCredentials.map((c: any) => ({
                id: Base64.toUint8Array(c.id),
                type: c.type,
            })),
        },
    });
    CONSOLE.debug("Received credential from browser", rawCredential);
    if (rawCredential === null) {
        ERROR_STORE.reportEnum(ErrorEnum.GettingCredentialsFailed);
        throw new Error(ErrorEnum.GettingCredentialsFailed);
    }

    // eslint-disable-next-line jsdoc/require-jsdoc
    const credential = rawCredential as PublicKeyCredential & { response: AuthenticatorAssertionResponse };

    return await ApiPublic.vpnAuth.completeWebauthn({
        id: credential.id,
        rawId: Base64.fromUint8Array(new Uint8Array(credential.rawId), true),
        response: {
            authenticatorData: Base64.fromUint8Array(new Uint8Array(credential.response.authenticatorData), true),
            clientDataJSON: Base64.fromUint8Array(new Uint8Array(credential.response.clientDataJSON), true),
            signature: Base64.fromUint8Array(new Uint8Array(credential.response.signature), true),
            userHandle: credential.response.userHandle,
        },
        type: credential.type,
    });
}

/**
 * Register a webauthn key
 *
 * @param type The type of the key to register
 * @param label The label of the key
 *
 * @returns A promise whether the request was successful
 */
export async function registerKey(type: "invitation" | "user", label: string): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const challenge: any = await (type === "invitation"
        ? ApiPublic.credentials.createWebauthnKey({ label })
        : ApiUser.credentials.createWebauthnKey({ label }));

    CONSOLE.warn("Received challenge from server", challenge);

    const rawCredential = await navigator.credentials.create({
        publicKey: {
            user: {
                id: Base64.toUint8Array(challenge.publicKey.user.id).slice(),
                name: challenge.publicKey.user.name,
                displayName: challenge.publicKey.user.displayName,
            },
            attestation: challenge.publicKey.attestation,
            rp: challenge.publicKey.rp,
            challenge: Base64.toUint8Array(challenge.publicKey.challenge).slice(),
            extensions: challenge.publicKey.extensions,
            timeout: challenge.publicKey.timeout,
            excludeCredentials:
                challenge.publicKey.excludeCredentials !== undefined
                    ? challenge.publicKey.excludeCredentials.map(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (c: any) => ({
                              id: Base64.toUint8Array(c.id),
                              type: c.type,
                          }),
                      )
                    : [],
            pubKeyCredParams: challenge.publicKey.pubKeyCredParams,
            authenticatorSelection: challenge.publicKey.authenticatorSelection,
        },
    });
    CONSOLE.warn("Created credential from browser", rawCredential);
    if (rawCredential === null) {
        ERROR_STORE.reportEnum(ErrorEnum.RequestingNewCredentialsFailed);
        return;
    }

    // eslint-disable-next-line jsdoc/require-jsdoc
    const credential = rawCredential as PublicKeyCredential & { response: AuthenticatorAttestationResponse };

    const body = {
        id: credential.id,
        rawId: Base64.fromUint8Array(new Uint8Array(credential.rawId), true),
        response: {
            attestationObject: Base64.fromUint8Array(new Uint8Array(credential.response.attestationObject), true),
            clientDataJSON: Base64.fromUint8Array(new Uint8Array(credential.response.clientDataJSON), true),
        },
        type: credential.type,
    };
    return await (type === "invitation"
        ? ApiPublic.credentials.completeWebauthn(body)
        : ApiUser.credentials.completeWebauthn(body));
}
